import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil, finalize } from 'rxjs';
import { Brand } from '@app/core/types/brand';
import {
  ChangeDirection,
  ClientPricingOffer,
  ClientPricingOfferValueType,
  OfferAccrualCode,
  ClientPricingOfferDto
} from '@app/core/types/client-pricing-offer';
import { ClientPricingOfferService } from '@app/core/services/client-pricing-offer.service';
import { NotificationService } from '@app/core/services/notification.service';
import { CreditAccrualsService } from '@app/core/services/credit-accruals.service';
import { IdentifierValidation } from '@app/core/validation/identifier-validation';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { ItfgDateAdapter } from '@app/core/adapters/date-adapter.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Client } from '@app/core/types/client';
import { AccrualCode } from '@app/core/types/accrual';

const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'itfg-create-pricing-offer-create-dialog',
  templateUrl: './client-pricing-offer-create-dialog.component.html',
  styleUrls: ['./client-pricing-offer-create-dialog.component.scss'],
  standalone: false,
  providers: [
    { provide: DateAdapter, useClass: ItfgDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ]
})
export class ClientPricingOfferCreateDialogComponent implements OnInit, OnDestroy {
  offerForm: FormGroup;
  brands: Brand[] = [];
  clientPricingOfferValuesTypes: typeof ClientPricingOfferValueType = ClientPricingOfferValueType;
  changeDirections: typeof ChangeDirection = ChangeDirection;
  valueTypeValues = Object.values(ClientPricingOfferValueType);
  changeDirectionValues = Object.values(ChangeDirection);
  accrualTypes: AccrualCode[] = [];
  isLoading: boolean = false;
  currentDate: Date = new Date();
  tomorrowDate: Date = new Date(Date.now() + 24 * 60 * 60 * 1000);
  _unsubscribe = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private clientPricingOfferService: ClientPricingOfferService,
    private notificationService: NotificationService,
    private accrualService: CreditAccrualsService,
    public dialogRef: MatDialogRef<ClientPricingOfferCreateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      client: Client;
    }
  ) {
    this.offerForm = this.createForm();
  }

  ngOnInit(): void {
    this.loadAccrualTypes();

    if (this.data && this.data.client) {
      this.offerForm.patchValue({
        civilId: this.data.client.civilId,
        selectedBrand: this.data.client.brand
      });
    }
  }

  createForm(): FormGroup {
    return this.fb.group({
      civilId: ['', [Validators.required, this.civilIdValidator]],
      selectedBrand: [null, Validators.required],
      accrualTypeCode: [null, Validators.required],
      value: [null, [Validators.required, Validators.min(0)]],
      valueType: [ClientPricingOfferValueType.PERCENT, Validators.required],
      changeDirection: [ChangeDirection.DECREASE, Validators.required],
      utilizationsLimit: [null, [Validators.min(1), Validators.required]],
      validFrom: [new Date(), Validators.required],
      validTo: [null, Validators.required],
      note: [null]
    });
  }

  loadAccrualTypes() {
    this.clientPricingOfferService.getClientPricingAccrualTypes$().pipe(
      takeUntil(this._unsubscribe)
    ).subscribe((offerAccrualTypes: AccrualCode[]) => {
      this.accrualTypes = offerAccrualTypes;

      const forfeitAccrualType = this.accrualTypes.find((accrualType: AccrualCode) => accrualType === AccrualCode.FORFEIT)

      if (forfeitAccrualType) {
        this.offerForm.get('accrualTypeCode').patchValue(forfeitAccrualType)
      }
    })
  }

  civilIdValidator(control: AbstractControl) {
    if (!control.value) return null;
    return IdentifierValidation.isValidEgn(control);
  }

  compareBrands(firstBrand: Brand, secondBrand: Brand): boolean {
    return firstBrand?.id === secondBrand?.id;
  }

  onSubmit(): void {
    this.offerForm.markAllAsTouched();

    if (this.offerForm.invalid) {
      this.notificationService.showLocalizedErrorMessage({
        notificationText: 'news.messages.requiredFields'
      });
      return;
    }

    this.createOffer();
  }

  createOffer(): void {
    this.isLoading = true;
    const formValue = this.offerForm.value;

    const offerDto: ClientPricingOfferDto = {
      offer: {
        accrualTypeId: this.accrualService.mapCodeToTypeId(formValue.accrualTypeCode),
        accrualTypeCode: formValue.accrualTypeCode,
        utilizationsLimit: formValue.utilizationsLimit,
        validFrom: formValue.validFrom.toISOString(),
        validTo: formValue.validTo.toISOString(),
        value: formValue.value,
        valueType: formValue.valueType,
        changeDirection: formValue.changeDirection

      },
      client: {
        civilId: formValue.civilId,
        brandCode: formValue.selectedBrand.code
      }
    };

    this.clientPricingOfferService
      .createClientPricingOffer$(offerDto)
      .pipe(
        finalize(() => this.isLoading = false),
        takeUntil(this._unsubscribe)
      )
      .subscribe({
        next: (createdOffer: ClientPricingOffer) => {
          this.notificationService.showLocalizedSuccessMessage({
            notificationText: 'clientPricingOffers.offerCreatedSuccessfully'
          });
          this.dialogRef.close(createdOffer);
        },
        error: (error: HttpErrorResponse) => {
          this.handleCreateError(error);
        }
      });
  }

  handleCreateError(error: HttpErrorResponse): void {
    if (error.error?.code) {
      this.notificationService.showLocalizedErrorMessage({
        notificationText: `clientPricingOffers.errors.${error.error.code.toLowerCase()}`
      });
    } else {
      this.notificationService.showLocalizedErrorMessage({
        notificationText: 'clientPricingOffers.createOfferErrorMessage'
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}